import React from "react";
import api from "../../lib/api";
import { TrendingUp, ShoppingBag, Users, BarChart2, Package, Star, Award } from "lucide-react";

const PERIODS = [
    { key: "day", label: "Azi" },
    { key: "week", label: "7 zile" },
    { key: "month", label: "30 zile" },
    { key: "quarter", label: "90 zile" },
    { key: "year", label: "12 luni" },
];

function Stat({ label, value, icon: Icon, color = "primary" }) {
    return (
        <div className="card-soft p-5 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-${color}/10`}>
                <Icon className={`h-6 w-6 text-${color}`} />
            </div>
            <div>
                <div className="text-2xl font-heading font-bold">{value}</div>
                <div className="text-sm text-muted-foreground">{label}</div>
            </div>
        </div>
    );
}

function MiniBar({ items, valKey, labelKey, color = "#7c3aed", maxItems = 8 }) {
    const top = items.slice(0, maxItems);
    const max = Math.max(...top.map(x => x[valKey] || 0), 1);
    return (
        <div className="space-y-2">
            {top.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                    <div className="w-4 text-xs text-muted-foreground text-right shrink-0">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs truncate max-w-[160px]">{item[labelKey]}</span>
                            <span className="text-xs font-medium shrink-0 ml-2">
                                {typeof item[valKey] === "number"
                                    ? item[valKey] % 1 === 0 ? item[valKey] : item[valKey].toFixed(2) + " RON"
                                    : item[valKey]}
                            </span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(item[valKey] / max) * 100}%`, background: color }} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function RevenueChart({ daily }) {
    if (!daily || !daily.length) return <div className="text-sm text-muted-foreground py-8 text-center">Fără date</div>;
    const max = Math.max(...daily.map(d => d.revenue), 1);
    const W = 100 / daily.length;
    return (
        <div className="relative h-36 flex items-end gap-px">
            {daily.map((d, i) => (
                <div key={i} title={`${d.date}: ${d.revenue.toFixed(2)} RON (${d.orders} comenzi)`}
                     className="flex-1 bg-primary/80 hover:bg-primary rounded-t transition-colors cursor-default"
                     style={{ height: `${Math.max(3, (d.revenue / max) * 100)}%` }} />
            ))}
        </div>
    );
}

export default function AdminAnalytics() {
    const [period, setPeriod] = React.useState("month");
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        setLoading(true);
        api.get("/admin/analytics/summary", { params: { period } })
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [period]);

    const fmt = (n) => typeof n === "number" ? n.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";

    return (
        <div data-testid="admin-analytics">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl">Rapoarte & Analytics</h1>
                </div>
                <div className="flex gap-1.5 bg-muted p-1 rounded-xl">
                    {PERIODS.map(p => (
                        <button key={p.key} onClick={() => setPeriod(p.key)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${period === p.key ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="text-muted-foreground py-20 text-center">Se încarcă...</div>
            ) : !data ? (
                <div className="text-muted-foreground py-20 text-center">Eroare la încărcare</div>
            ) : (
                <div className="space-y-6">
                    {/* KPI row */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Stat label="Venituri" value={`${fmt(data.totals.revenue)} RON`} icon={TrendingUp} />
                        <Stat label="Comenzi" value={data.totals.orders} icon={ShoppingBag} color="blue-600" />
                        <Stat label="Valoare medie" value={`${fmt(data.totals.avg_order)} RON`} icon={BarChart2} color="emerald-600" />
                        {data.margin ? (
                            <Stat label={`Marjă profit (${data.margin.margin_pct}%)`}
                                  value={`${fmt(data.margin.profit)} RON`} icon={Star} color="amber-500" />
                        ) : (
                            <Stat label="Clienți unici" value={data.top_clients.length} icon={Users} color="purple-600" />
                        )}
                    </div>

                    {/* Revenue chart */}
                    <div className="card-soft p-5">
                        <h3 className="font-heading text-lg mb-4">Venituri pe zile</h3>
                        <RevenueChart daily={data.daily} />
                        <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                            <span>{data.daily[0]?.date || ""}</span>
                            <span>{data.daily[data.daily.length - 1]?.date || ""}</span>
                        </div>
                    </div>

                    {/* Tables row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Top produse */}
                        <div className="card-soft p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Package className="h-4 w-4 text-primary" />
                                <h3 className="font-heading text-base">Top produse vândute</h3>
                            </div>
                            {data.top_products.length === 0
                                ? <p className="text-sm text-muted-foreground">Fără date</p>
                                : <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
                                            <th className="pb-2 text-left">Produs</th>
                                            <th className="pb-2 text-right">Cant.</th>
                                            <th className="pb-2 text-right">Venituri</th>
                                        </tr></thead>
                                        <tbody>{data.top_products.map((p, i) => (
                                            <tr key={i} className="border-b border-border/40 last:border-0">
                                                <td className="py-2 pr-2 max-w-[180px] truncate">{p.name}</td>
                                                <td className="py-2 text-right font-medium">{p.qty}</td>
                                                <td className="py-2 text-right text-primary">{fmt(p.revenue)} RON</td>
                                            </tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                            }
                        </div>

                        {/* Top clienți */}
                        <div className="card-soft p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Users className="h-4 w-4 text-primary" />
                                <h3 className="font-heading text-base">Top clienți (perioadă)</h3>
                            </div>
                            {data.top_clients.length === 0
                                ? <p className="text-sm text-muted-foreground">Fără date</p>
                                : <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
                                            <th className="pb-2 text-left">Client</th>
                                            <th className="pb-2 text-right">Comenzi</th>
                                            <th className="pb-2 text-right">Total</th>
                                        </tr></thead>
                                        <tbody>{data.top_clients.map((c, i) => (
                                            <tr key={i} className="border-b border-border/40 last:border-0">
                                                <td className="py-2 pr-2">
                                                    <div className="font-medium truncate max-w-[140px]">{c.name || "—"}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                                                </td>
                                                <td className="py-2 text-right">{c.orders}</td>
                                                <td className="py-2 text-right text-primary">{fmt(c.total)} RON</td>
                                            </tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                            }
                        </div>

                        {/* Per categorie */}
                        <div className="card-soft p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <BarChart2 className="h-4 w-4 text-primary" />
                                <h3 className="font-heading text-base">Venituri pe categorii</h3>
                            </div>
                            <MiniBar items={data.category_revenue} valKey="revenue" labelKey="name" color="#7c3aed" />
                        </div>

                        {/* LTV all-time */}
                        <div className="card-soft p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="h-4 w-4 text-amber-500" />
                                <h3 className="font-heading text-base">Top clienți (LTV total)</h3>
                            </div>
                            {data.top_ltv.length === 0
                                ? <p className="text-sm text-muted-foreground">Fără date</p>
                                : <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead><tr className="border-b border-border text-xs text-muted-foreground uppercase tracking-wide">
                                            <th className="pb-2 text-left">Client</th>
                                            <th className="pb-2 text-right">Comenzi</th>
                                            <th className="pb-2 text-right">LTV</th>
                                        </tr></thead>
                                        <tbody>{data.top_ltv.map((c, i) => (
                                            <tr key={i} className="border-b border-border/40 last:border-0">
                                                <td className="py-2 pr-2">
                                                    <div className="font-medium truncate max-w-[140px]">{c.name || "—"}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                                                </td>
                                                <td className="py-2 text-right">{c.orders}</td>
                                                <td className="py-2 text-right font-semibold text-amber-600">{fmt(c.ltv)} RON</td>
                                            </tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                            }
                        </div>
                    </div>

                    {/* Brand breakdown */}
                    {data.brand_revenue.length > 0 && (
                        <div className="card-soft p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Star className="h-4 w-4 text-primary" />
                                <h3 className="font-heading text-base">Venituri pe branduri</h3>
                            </div>
                            <MiniBar items={data.brand_revenue} valKey="revenue" labelKey="brand" color="#0ea5e9" />
                        </div>
                    )}

                    {/* Margin details */}
                    {data.margin && (
                        <div className="card-soft p-5 bg-emerald-50/50 border border-emerald-200">
                            <h3 className="font-heading text-base mb-3 text-emerald-900">Marjă profit (produse cu preț achiziție)</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div><div className="text-muted-foreground">Venituri</div><div className="font-semibold text-lg">{fmt(data.margin.revenue)} RON</div></div>
                                <div><div className="text-muted-foreground">Cost achiziție</div><div className="font-semibold text-lg">{fmt(data.margin.cost)} RON</div></div>
                                <div><div className="text-muted-foreground">Profit brut</div><div className="font-semibold text-lg text-emerald-700">{fmt(data.margin.profit)} RON</div></div>
                                <div><div className="text-muted-foreground">Marjă</div><div className="font-semibold text-lg text-emerald-700">{data.margin.margin_pct}%</div></div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
